import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';

interface LowCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCredits: number;
  requiredCredits?: number;
  isBlocker?: boolean; // If true, it's "insufficient credits" mode. If false, it's "low balance warning" mode.
}

export default function LowCreditModal({
  isOpen,
  onClose,
  currentCredits = 0,
  requiredCredits = 1,
  isBlocker = false,
}: LowCreditModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const safeCurrent = (currentCredits ?? 0).toLocaleString();
  const safeRequired = (requiredCredits ?? 0).toLocaleString();

  const handleBuyCredits = () => {
    onClose();
    navigate('/credits');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-3xl border border-slate-100 shadow-2xl w-full max-w-sm p-8 relative animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Warning Icon */}
        <div className="w-14 h-14 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-7 h-7" />
        </div>

        {/* Title & Description */}
        <div className="text-center space-y-2 mb-6">
          <h3 className="text-xl font-bold text-slate-900 leading-snug">
            {isBlocker ? "Insufficient Credits" : "Your credit balance is running low"}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed px-1">
            {isBlocker 
              ? `This operation requires ${safeRequired} credits, but you only have ${safeCurrent} left. Top up to keep publishing without interruption.`
              : "You have less than 100 credits left. Video posts, carousels, SEO blogs/articles, and website builds all draw from this balance — top up to keep publishing without interruption."
            }
          </p>
        </div>

        {/* Balance Card */}
        <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4 flex items-center justify-between mb-6">
          <span className="text-xs font-semibold text-slate-500">Remaining balance</span>
          <span className="text-sm font-bold text-red-600">{safeCurrent} credits</span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-[#f1f5f9] hover:bg-[#e2e8f0] rounded-2xl text-xs font-bold text-slate-600 text-center transition-colors cursor-pointer"
          >
            {isBlocker ? "Cancel" : "Remind Me Later"}
          </button>
          <button
            onClick={handleBuyCredits}
            className="flex-1 py-3 bg-[#0859b8] hover:bg-[#06428b] rounded-2xl text-xs font-bold text-white text-center transition-colors cursor-pointer shadow-sm shadow-blue-100"
          >
            Buy Credits
          </button>
        </div>
      </div>
    </div>
  );
}
